/**
 * Application Route Table
 *
 * Single source of truth for the app's routes, consumed by:
 *
 * - `src/main.tsx` — passed to `ViteReactSSG({ routes })`, which builds the
 *   `react-router` data router used for both static generation (build
 *   time) and client-side hydration/navigation (runtime).
 * - `src/__tests__/ssg.test.ts` — structural assertions about which routes
 *   exist and which are eligible for static generation.
 *
 * ## Two route groups
 *
 * 1. **Marketing / public (→ SSG)** — flat, top-level routes with no
 *    layout wrapper. They render standalone (no `ConfigProvider` /
 *    `AuthProvider`) so `vite-react-ssg` can pre-render them to plain
 *    static HTML at build time. The exact list that gets pre-rendered is
 *    controlled by `MARKETING_ROUTES` in `./marketingRoutes.ts` +
 *    `ssgOptions.includedRoutes` in `vite.config.ts` — see that file for
 *    how to add/remove a route from static generation.
 * 2. **SPA shell (auth, protected app, admin)** — nested under the
 *    pathless `AppShell` layout route, which provides runtime config and
 *    Supabase auth context. These routes are never statically generated
 *    (they are absent from `MARKETING_ROUTES`) and always behave as a
 *    normal client-rendered SPA, including dynamic routes such as
 *    `/admin/jobs/:jobId`.
 *
 * See docs/v2/06-frontend-geo.md for the broader SSG/GEO plan.
 */
import type { RouteRecord } from 'vite-react-ssg';
import { Navigate } from 'react-router-dom';

import AppShell from './AppShell';
import { ProtectedRoute } from './ProtectedRoute';

// Marketing / public pages (statically generated — see marketingRoutes.ts)
import HomePage from '../pages/Home';
import HowItWorksPage from '../pages/marketing/HowItWorks';
import FeaturesPage from '../pages/marketing/Features';
import ForStartupsPage from '../pages/marketing/ForStartups';
import AboutPage from '../pages/marketing/About';
import CareersPage from '../pages/marketing/Careers';
import ContactPage from '../pages/marketing/Contact';
import HelpCenterPage from '../pages/marketing/HelpCenter';
import PrivacyPage from '../pages/marketing/Privacy';
import TermsPage from '../pages/marketing/Terms';
import ThankYouPage from '../pages/marketing/ThankYou';
import GetStartedPage from '../pages/marketing/GetStarted';

// Auth pages (client-only SPA — interactive, no GEO value)
import LoginPage from '../pages/Login';
import SignupPage from '../pages/Signup';
import ForgotPasswordPage from '../pages/ForgotPassword';
import ResetPasswordPage from '../pages/ResetPassword';

// Authenticated app (client-only SPA, behind ProtectedRoute)
import DashboardPage from '../pages/Dashboard';
import AccountSubscriptionPage from './routes/account/Subscription';
import JobPublishedPage from './routes/JobPublished';
import PublicJobPage from './routes/PublicJobPage';
import SupportPage from './routes/Support';

// Admin (client-only SPA, behind ProtectedRoute + AdminGuard)
import AdminGuard from '../components/admin/AdminGuard';
import AdminDashboard from './routes/admin/AdminDashboard';
import CustomersList from './routes/admin/CustomersList';
import JobsList from './routes/admin/JobsList';
import JobDetail from './routes/admin/JobDetail';
import AdminSubscriptions from './routes/admin/Subscriptions';
import AdminCustomerHealth from './routes/admin/CustomerHealth';
import AdminSendingDomains from './routes/admin/SendingDomains';

/**
 * Route tree passed to `ViteReactSSG`/`createBrowserRouter`.
 *
 * NOTE: the marketing routes below are intentionally flat siblings (no
 * shared layout route) so they render with zero providers during SSG.
 * The SPA shell routes are nested under the pathless `AppShell` layout.
 */
export const routes: RouteRecord[] = [
  // ---------------------------------------------------------------------
  // Marketing / public routes — statically generated (see MARKETING_ROUTES
  // in ./marketingRoutes.ts, kept in exact sync with this list).
  // ---------------------------------------------------------------------
  { path: '/', Component: HomePage },
  { path: '/how-it-works', Component: HowItWorksPage },
  { path: '/features', Component: FeaturesPage },
  { path: '/for-startups', Component: ForStartupsPage },
  { path: '/about', Component: AboutPage },
  { path: '/careers', Component: CareersPage },
  { path: '/contact', Component: ContactPage },
  { path: '/help-center', Component: HelpCenterPage },
  { path: '/privacy', Component: PrivacyPage },
  { path: '/terms', Component: TermsPage },
  { path: '/thank-you', Component: ThankYouPage },
  { path: '/getstarted', Component: GetStartedPage },

  // ---------------------------------------------------------------------
  // SPA shell — auth, protected app, and admin routes. Never statically
  // generated; always client-side rendered. Dynamic routes (e.g.
  // `/admin/jobs/:jobId`) live here and are excluded from SSG by
  // definition (see `isDynamicRoutePath` in ./marketingRoutes.ts).
  // ---------------------------------------------------------------------
  {
    Component: AppShell,
    children: [
      { path: '/login', Component: LoginPage },
      { path: '/signup', Component: SignupPage },
      { path: '/forgot-password', Component: ForgotPasswordPage },
      { path: '/auth/reset-password', Component: ResetPasswordPage },

      {
        path: '/dashboard',
        Component: () => <ProtectedRoute element={<DashboardPage />} />,
      },

      // Sourcing progress tracker, shown right after publishing a job
      // (GitHub issue #498 — "E2E: Sourcing & outreach"). See
      // `./routes/JobPublished.tsx`'s module doc-comment.
      {
        path: '/job-published',
        Component: () => <ProtectedRoute element={<JobPublishedPage />} />,
      },

      // Public job page (GitHub issue #301 — "[GEO] JobPosting schema
      // markup on public job pages"). Deliberately NOT wrapped in
      // `ProtectedRoute` — reachable by anonymous candidates and search
      // crawlers. A dynamic route (`:jobId`), so — like
      // `/admin/jobs/:jobId` below — it's automatically excluded from
      // `vite-react-ssg` static generation (see
      // `./marketingRoutes.ts`'s `isDynamicRoutePath`); it still mounts
      // under `AppShell` (not the flat marketing group) purely to reuse
      // the shared `QueryClientProvider`/`Toaster` `AppShell` already
      // provides for `usePublicJob`'s TanStack Query hook — see
      // `./routes/PublicJobPage.tsx`'s module doc-comment.
      {
        path: '/jobs/:jobId',
        Component: () => <PublicJobPage />,
      },

      // Unified API client hook (`useSubscriptionMe`) — see
      // `./routes/account/Subscription.tsx`'s module doc-comment.
      {
        path: '/account/subscription',
        Component: () => <ProtectedRoute element={<AccountSubscriptionPage />} />,
      },

      // Internal support ticket form (GitHub issue #544 — "Slack
      // workspace invites failing for customers — domain restriction").
      // Replaces the Slack shared-invite link as the support channel; see
      // `./routes/Support.tsx`'s module doc-comment. Same single path for
      // every customer, including one who never received the Slack
      // invite at all (`customers.invite_sent_at IS NULL`).
      {
        path: '/support',
        Component: () => <ProtectedRoute element={<SupportPage />} />,
      },

      {
        path: '/admin',
        Component: () => (
          <ProtectedRoute
            element={
              <AdminGuard>
                <AdminDashboard />
              </AdminGuard>
            }
          />
        ),
      },
      {
        path: '/admin/customers',
        Component: () => (
          <ProtectedRoute
            element={
              <AdminGuard>
                <CustomersList />
              </AdminGuard>
            }
          />
        ),
      },
      {
        path: '/admin/jobs',
        Component: () => (
          <ProtectedRoute
            element={
              <AdminGuard>
                <JobsList />
              </AdminGuard>
            }
          />
        ),
      },
      {
        path: '/admin/jobs/:jobId',
        Component: () => (
          <ProtectedRoute
            element={
              <AdminGuard>
                <JobDetail />
              </AdminGuard>
            }
          />
        ),
      },
      {
        path: '/admin/subscriptions',
        Component: () => (
          <ProtectedRoute
            element={
              <AdminGuard>
                <AdminSubscriptions />
              </AdminGuard>
            }
          />
        ),
      },
      {
        path: '/admin/customer-health',
        Component: () => (
          <ProtectedRoute
            element={
              <AdminGuard>
                <AdminCustomerHealth />
              </AdminGuard>
            }
          />
        ),
      },
      {
        // "[Email] Move cold outreach off the brand domain — dedicated,
        // warmed, rotated sending domains" (#188/#516): active
        // cold-outreach rotation domains + per-domain SPF/DKIM/DMARC
        // verification status.
        path: '/admin/sending-domains',
        Component: () => (
          <ProtectedRoute
            element={
              <AdminGuard>
                <AdminSendingDomains />
              </AdminGuard>
            }
          />
        ),
      },

      // GitHub issue #548 ("bug: /admin/dashboard returns 404 — route is
      // /admin"): `/admin/dashboard` isn't a registered path anywhere
      // above, so before this fix it fell through to the catch-all `*`
      // route below. This plain, unguarded redirect to the real `/admin`
      // path is deliberately scoped to this one path only (it does NOT
      // touch the catch-all, so any other unknown `/admin/*` path — e.g.
      // `/admin/does-not-exist` — still falls through unaffected). It
      // doesn't need its own `ProtectedRoute`/`AdminGuard` wrapping: once
      // react-router lands on `/admin`, that route's own
      // `ProtectedRoute`+`AdminGuard` (below) re-run in full, so a
      // non-superuser hitting `/admin/dashboard` still gets redirected to
      // `/dashboard?admin_access_denied=1` exactly as if they'd typed
      // `/admin` directly — the guard behavior is preserved, not
      // bypassed. `replace` (not a push) means the `/admin/dashboard`
      // history entry is swapped for `/admin` rather than added on top of
      // it, so the browser back button from `/admin` goes to whatever
      // page preceded the deep link, not back into a redirect loop.
      { path: '/admin/dashboard', Component: () => <Navigate to="/admin" replace /> },

      // Catch-all redirect (client-only; not part of MARKETING_ROUTES)
      { path: '*', Component: () => <Navigate to="/" replace /> },
    ],
  },
];

export default routes;
